// libraries
import React, { useState, useEffect } from 'react'
import clsx from 'clsx'

// components
import Section from '/react4xp/components/Section'
import Link from '/react4xp/components/Link'
import MasonryGrid from '/react4xp/components/MasonryGrid'
import IdeaCard from '/react4xp/components/Idea/IdeaCard'

// utils
import { isNonWhitespaceString } from '/lib/reactutils/utils.js'

// svg
import { UxArrowRight } from '/react4xp/components/Svg/Ux/Arrow'

export interface AllIdeasProps {
	section?: {
		bgColor?: string
		paddingTop?: 'big' | 'small' | 'smaller' | 'medium' | 'none'
		paddingBottom?: 'big' | 'small' | 'smaller' | 'medium' | 'none'
		id?: string
		className?: string
	}
	isWhite?: boolean
	title?: string
	totalText?: {
		before?: string
		of?: string
		text?: string
	}
	viewAll?: {
		text?: string
		href?: string
		external?: boolean
	}
	loadMore?: {
		text?: string
		onClick?: () => void,
		leftAligned?: boolean
		isWhite?: boolean
	},
	ideasLiked?: string[],
	endpoint?: string,
	services: {
		likeContent: string
	},
	startValues: {
		page?: number
		pageSize?: string
		status?: string
		packaging?: string
		taste?: string
		category?: string
		region?: string
		sort?: string
	}
	isLogged?: boolean
}

export default function AllIdeas({
	section,
	isWhite,
	title,
	totalText,
	viewAll,
	loadMore,
	endpoint,
	startValues,
	ideasLiked,
	services,
	isLogged
}: AllIdeasProps) {
	const [ideas, setIdeas] = useState<any[]>([])
	const [page, setPage] = useState(startValues?.page || 1)
	const [hasNextPage, setHasNextPage] = useState(false)
	const [shouldLoadMore, setShouldLoadMore] = useState(false)
	const [total, setTotal] = useState(0)

	useEffect(() => {
		handleFetchIdeas()
	}, [page])

	const handleFetchIdeas = async ()=> {
		if(!endpoint) return;

		const origin = window.location.origin;
		const url = new URL(`${origin}${endpoint}`);
		const queryParams = new URLSearchParams();

		queryParams.append('type', 'nominations')
		if (page) queryParams.append('page', String(page));
		if (startValues?.pageSize) queryParams.append('pageSize', startValues?.pageSize);
		if (startValues?.status) queryParams.append('status', startValues?.status);
		if (startValues?.packaging) queryParams.append('packaging', startValues?.packaging);
		if (startValues?.taste) queryParams.append('taste', startValues?.taste);
		if (startValues?.category) queryParams.append('category', startValues?.category);
		if (startValues?.region) queryParams.append('region', startValues?.region);
		if (startValues?.sort) queryParams.append('sort', startValues?.sort);

		url.search = queryParams.toString();

		try {
			const response = await fetch(url);

			if (!response.ok) {
				throw new Error(`Network response was not ok: ${response.statusText}`);
			}

			const data = await response.json();

			if(shouldLoadMore){
				setIdeas([...ideas, ...data.hits]);
				setShouldLoadMore(false)
			} else {
				setIdeas(data.hits);
			}

			setHasNextPage(data.hasNextPage);
			setTotal(data.total)

		} catch (error) {
			console.error('There was a problem with the fetch operation:', error);
		}
	}

	const handleLoadMoreIdeas = () => {
		if(hasNextPage){
			setShouldLoadMore(true)
			setPage(page + 1)
		}
	}

    return (
        <Section
			bgColor={section?.bgColor}
			paddingTop={section?.paddingTop}
			paddingBottom={section?.paddingBottom}
			className={clsx('all-ideas', section?.className)}
			id={section?.id}
        >
            <div className='container'>

                {isNonWhitespaceString(title) && (
                    <h2 className={clsx(
                        'h2 all-ideas__title',
                        isWhite && 'white'
                    )}>
                        {title}
                    </h2>
                )}

                <div className='all-ideas__details'>

					{totalText && (
						<div className='total'>
							<p className={clsx(isWhite && 'white')}>
								{totalText?.before} {ideas.length} {totalText?.of} {total} {totalText?.text}
							</p>
						</div>
					)}

					{isNonWhitespaceString(viewAll?.text) && (
						<Link
							href={viewAll?.href}
							text={viewAll?.text}
							iconPosition='right'
							icon={<UxArrowRight />}
							isWhite={isWhite}
						/>
					)}

                </div>

                {ideas ? (
                    <div className='all-ideas__grid'>

                        <MasonryGrid
                            items={ideas || []}
                            columnGutter={20}
                            maxColumnCount={3}
                            breakpointCols={{
                                default: 3,
                                1200: 2,
                                676: 1
                            }}
                            columnWidth={275}
                            overscanBy={99}
                            render={({ data }) => 
								<IdeaCard
									image={data.image?.link}
									alt={data.image?.alt}
									status={data.statusData?.status}
									statusText={data.statusData?.label}
									title={data.title}
									desc={data.description}
									extraField={data.extraField}
									likes={data.likes}
									isLiked={ideasLiked.includes(data.id)}
									comments={data.comments}
									href={data.url?.href}
									contentId={data.id}
									services={services}
									isLogged={isLogged}
									isInteractive
								/>
                          	}
                        />

                        {hasNextPage && (
                            <div className={clsx(
                                'load-more',
                                loadMore?.leftAligned && 'load-more--left-aligned'
                            )}>
                                <Link
                                    text={loadMore.text}
                                    onClick={handleLoadMoreIdeas}
                                    iconPosition='right'
                                    icon={<UxArrowRight />}
                                    noMotionMobile
                                    isWhite={loadMore?.isWhite}
                                />
                            </div>
                        )}

                    </div>
                ) : (
                    <div className='all-ideas__no-results'>
                        <p className={clsx(
                            'h3',
                            isWhite && 'white'
                        )}>
                            Ingen idéer funnet
                        </p>
                    </div>
                )}
                
            </div>
        </Section>
    )
}